import type { LoaderFunction } from 'react-router-dom';
import { client } from '~/api/client';
import { queryData } from '~/api/data/request';
import { getEmailDraftTemplate } from '~/api/data/email-templates/queries';
import { loadQuery } from '~/api/data/responses';
import type { DataResult } from '~/api/data/result';
import {
  BulkDraftEmailsDocument,
  type BulkDraftEmailsQuery,
  type BulkDraftEmailsQueryVariables,
  EmailBulkDraftDocument,
  type EmailBulkDraftFieldsFragment,
  type EmailBulkDraftQuery,
  type EmailBulkDraftQueryVariables,
  EmailBulkSendRecipientsDocument,
  type EmailBulkSendRecipientsQuery,
  type EmailBulkSendRecipientsQueryVariables,
  EmailBulkSendsDocument,
  type EmailBulkSendListFieldsFragment,
  type EmailBulkSendsQuery,
  type EmailBulkSendsQueryVariables
} from '~/api/graphql';

/**
 * As many bulk emails as the server lists at once. Outgoing reads the bulk drafts apart from
 * the rest, so sent batches don't crowd drafts out; past this many of either, the oldest are
 * left out.
 */
export const EMAIL_BULK_SENDS_LIMIT = 50;

export const getEmailBulkSends = () =>
  queryData<EmailBulkSendsQuery, EmailBulkSendsQueryVariables, EmailBulkSendListFieldsFragment[]>(
    client,
    { query: EmailBulkSendsDocument, variables: { first: EMAIL_BULK_SENDS_LIMIT } },
    (data) => data?.emailBulkSends
  );

export type BulkDraft = EmailBulkDraftFieldsFragment;

export type BulkDraftEmail = {
  subject: string;
  /** The newest version, which Open shows. */
  version: number;
  templateId: string | null;
  hasCc: boolean;
  hasBcc: boolean;
};

export type BulkDraftPageData = {
  /** Null when it's gone or the reader can't see its workspace. */
  bulkDraft: BulkDraft | null;
  emailByArtifactId: Record<string, BulkDraftEmail>;
  /** The names of the templates the emails were drafted from, where the reader can see them. */
  templateNameById: Record<string, string>;
  /** Once sent, how far its batch has got; null before Send, or when that read failed. */
  batch: SentBatch | null;
};

/** A sent bulk email's progress, with where each recipient's email stands. */
export type SentBatch = NonNullable<EmailBulkSendRecipientsQuery['emailBulkSend']>;
export type SentRecipient = SentBatch['recipients'][number];

/** Set on a read that must not join one already in flight, which may predate a change. */
type ReadOptions = { isFresh?: boolean };

const readContext = ({ isFresh = false }: ReadOptions) =>
  isFresh ? { queryDeduplication: false } : undefined;

export const getSentBatch = (bulkSendId: string, options: ReadOptions = {}) =>
  queryData<EmailBulkSendRecipientsQuery, EmailBulkSendRecipientsQueryVariables, SentBatch>(
    client,
    {
      query: EmailBulkSendRecipientsDocument,
      variables: { id: bulkSendId },
      context: readContext(options)
    },
    (data) => data?.emailBulkSend
  );

const getBulkDraftEmails = (artifactIds: string[]) =>
  queryData<BulkDraftEmailsQuery, BulkDraftEmailsQueryVariables, Record<string, BulkDraftEmail>>(
    client,
    { query: BulkDraftEmailsDocument, variables: { artifactIds } },
    (data) =>
      Object.fromEntries(
        (data?.caraWorkspaceArtifacts ?? []).map((artifact) => {
          const latest = artifact.versions.find(
            ({ version }) => version === artifact.latestVersion
          );
          const email = {
            subject: artifact.title,
            version: artifact.latestVersion,
            templateId: latest?.email?.template?.templateId ?? null,
            hasCc: !!latest?.email?.cc.length,
            hasBcc: !!latest?.email?.bcc.length
          };
          return [artifact.id, email];
        })
      )
  );

const NO_EMAILS: DataResult<Record<string, BulkDraftEmail>> = { data: {} };

/** A template whose name can't be read leaves it unnamed rather than failing the page. */
const getTemplateNames = async (templateIds: string[]): Promise<Record<string, string>> => {
  const reads = await Promise.allSettled(templateIds.map(getEmailDraftTemplate));
  return Object.fromEntries(
    reads.flatMap((read) =>
      read.status === 'fulfilled' && read.value !== 'hidden'
        ? [[read.value.id, read.value.name]]
        : []
    )
  );
};

/**
 * A bulk draft, the subject and template of each email Cara has drafted for it, and its batch
 * once sent.
 */
export const getBulkDraftPage = async (
  bulkSendId: string,
  options: ReadOptions = {}
): Promise<DataResult<BulkDraftPageData>> => {
  const { data, error } = await queryData<
    EmailBulkDraftQuery,
    EmailBulkDraftQueryVariables,
    BulkDraft | null
  >(
    client,
    {
      query: EmailBulkDraftDocument,
      variables: { id: bulkSendId },
      context: readContext(options)
    },
    (data) => data?.emailBulkSend ?? null
  );
  const bulkDraft = data ?? null;
  if (error || !bulkDraft) {
    return { data: { bulkDraft, emailByArtifactId: {}, templateNameById: {}, batch: null }, error };
  }
  const artifactIds = bulkDraft.recipients.flatMap((recipient) => recipient.artifactId ?? []);
  const [emails, sentBatch] = await Promise.all([
    artifactIds.length > 0 ? getBulkDraftEmails(artifactIds) : NO_EMAILS,
    // A batch that can't be read leaves the page to say so, rather than failing it.
    bulkDraft.sentAt ? getSentBatch(bulkDraft.id, options).catch(() => null) : null
  ]);
  const emailByArtifactId = emails.data ?? {};
  const templateIds = new Set(
    Object.values(emailByArtifactId).flatMap(({ templateId }) => templateId ?? [])
  );
  return {
    data: {
      bulkDraft,
      emailByArtifactId,
      templateNameById: await getTemplateNames([...templateIds]),
      batch: sentBatch?.data ?? null
    },
    error: emails.error
  };
};

export const loadBulkDraftPage: LoaderFunction = ({ params }) =>
  loadQuery(getBulkDraftPage(params.bulkSendId ?? ''));
